import {
  contractTypeLabel,
  EMPLOYEE_ROLE_LABELS,
  INVALID_EMAIL_MESSAGE,
  MISSING_FIRST_NAME_MESSAGE,
  MISSING_LAST_NAME_MESSAGE,
  NO_ROLE_LABEL,
  YARD_STATE_LABELS,
} from "@etudescarre/constants";
import { Button, cn, Modal } from "@etudescarre/ui";
import { isNil, isNotNil, isValidEmail } from "@etudescarre/utils";
import {
  ArrowLeftIcon,
  PencilSimpleIcon,
  PlusIcon,
  TrashIcon,
  UploadSimpleIcon,
} from "@phosphor-icons/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { toaster } from "@/app";
import { fetchAllCompanies } from "@/features/admin/components/fetch-all-companies";
import { fetchAllEmployees } from "@/features/admin/components/fetch-all-employees";
import { DeactivateCollaboratorModal } from "@/features/admin/deactivate-collaborator-modal";
import { apiClient } from "@/lib/api/client";
import {
  getYardState,
  getYardStateDateLabel,
  type YardState,
  type YardStateInput,
  yardStateBadgeClass,
} from "./yard-state";

type Company = {
  id: number;
  name: string;
  logoUrl: string | null;
  createdAt: string;
  hasCommissionedContract: boolean;
};

type Employee = {
  id: string;
  companyId: number;
  email: string | null;
  emailVerified: boolean;
  firstName: string | null;
  lastName: string | null;
  role: "root" | "admin" | "user" | null;
  isActive: boolean;
  lastLogin: string | null;
  invitedAt: string | null;
  dateJoined: string | null;
  createdAt: string;
  loginStatus: "active" | "invited" | "not_invited";
  hasCommissionedContract: boolean;
  /** Inactive but still referent on running contracts: roles to hand over. */
  pendingReferentRoles: number;
  company: { id: number; name: string };
};

type EmployeeContract = {
  employeeContractId: number;
  mail: string | null;
  isActive: boolean;
  contractType: string;
  yardId: number;
  yardName: string;
  yardIsLocked: boolean;
  yardComissioningDate: string | null;
  yardExpirationDate: string | null;
  yardBeforeArchivingDays: number | null;
};

function contractYard(contract: EmployeeContract): YardStateInput {
  return {
    isLocked: contract.yardIsLocked,
    expirationDate: contract.yardExpirationDate,
    beforeArchivingDays: contract.yardBeforeArchivingDays,
    comissioningDate: contract.yardComissioningDate,
  };
}

const YARD_STATE_ORDER: YardState[] = [
  "preparation",
  "en_service",
  "interrompu",
  "archive",
  "cloture",
];

// "none" = contact-only, no role assigned.
const NO_ROLE_VALUE = "none";

// A newly created collaborator's role is preselected from their email
// domain — Admin for @chantiercarre.fr, User otherwise — and stays
// editable afterwards.
const ADMIN_EMAIL_DOMAIN = "@chantiercarre.fr";
function defaultRoleForEmail(email: string): "admin" | "user" {
  return email.trim().toLowerCase().endsWith(ADMIN_EMAIL_DOMAIN)
    ? "admin"
    : "user";
}

// Empty passes: whether a mail is mandatory is the form's own rule.
function checkMailShape(mail: string): true | string {
  return mail === "" || isValidEmail(mail) || INVALID_EMAIL_MESSAGE;
}

function checkLastName(name: string): true | string {
  return name.trim() !== "" || MISSING_LAST_NAME_MESSAGE;
}

function checkFirstName(name: string): true | string {
  return name.trim() !== "" || MISSING_FIRST_NAME_MESSAGE;
}

/** Line under a field, reserved so the popup doesn't resize when it fills. */
function FieldError({ id, message }: { id: string; message?: string }) {
  return (
    <p id={id} className="mt-1 min-h-4 text-red-600 text-xs">
      {message}
    </p>
  );
}

function employeeDisplayName(emp: Employee): string {
  if (emp.firstName || emp.lastName) {
    return [emp.lastName, emp.firstName].filter(Boolean).join(" ");
  }
  return emp.email ?? "Sans nom";
}

function connectionLabel(emp: Employee): string {
  if (isNotNil(emp.lastLogin)) {
    return `Connecté le ${new Date(emp.lastLogin).toLocaleDateString("fr-FR")}`;
  }
  if (emp.loginStatus === "invited" && isNotNil(emp.invitedAt)) {
    return `Invité le ${new Date(emp.invitedAt).toLocaleDateString("fr-FR")}, jamais connecté`;
  }
  if (emp.loginStatus === "not_invited") return "Pas encore invité";
  return "Jamais connecté";
}

function useObjectUrl(file: File | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);
  return url;
}

/**
 * Logo picker for the create/edit company forms: shows a live preview of the
 * just-picked file, and accepts drag-and-drop in addition to the file-picker
 * button.
 */
function LogoUploadField({
  id,
  file,
  existingLogoUrl,
  onFileChange,
}: {
  id: string;
  file: File | null;
  existingLogoUrl?: string | null;
  onFileChange: (file: File | null) => void;
}) {
  const previewUrl = useObjectUrl(file);
  const [isDragOver, setIsDragOver] = useState(false);
  const displayUrl = previewUrl ?? existingLogoUrl ?? null;

  return (
    <div>
      <label
        htmlFor={id}
        className="mb-1 block text-sm font-medium text-zinc-900"
      >
        Logo
      </label>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: drag target wrapping the real file input below, which stays the keyboard/click way in */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragOver(false);
          const droppedFile = e.dataTransfer.files?.[0];
          if (droppedFile) onFileChange(droppedFile);
        }}
        className={cn(
          "flex items-center gap-3 rounded-md border border-dashed px-3 py-3",
          isDragOver ? "border-blue-400 bg-blue-50" : "border-zinc-300",
        )}
      >
        {displayUrl ? (
          <img
            src={displayUrl}
            alt="Logo"
            className="size-12 shrink-0 rounded border border-zinc-200 object-contain"
          />
        ) : (
          <UploadSimpleIcon className="size-6 shrink-0 text-zinc-300" />
        )}
        <div className="min-w-0 flex-1">
          <input
            id={id}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={(e) => onFileChange(e.target.files?.[0] ?? null)}
            className="w-full text-sm text-zinc-600 file:mr-3 file:rounded-md file:border-0 file:bg-zinc-100 file:px-3 file:py-1.5 file:text-sm hover:file:bg-zinc-200"
          />
          <p className="mt-1 text-xs text-zinc-400">
            PNG, JPEG ou WebP — 5 Mo max, ou glissez-déposez le fichier ici
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Up/Down arrow roving selection: given the item
 * currently selected in a list, returns the next/previous item, or null if
 * the key isn't an arrow key or the selection is already at the list's end.
 */
function moveSelection<T>(
  key: string,
  items: T[],
  currentId: string | number | null,
  idOf: (item: T) => string | number,
): T | null {
  if (key !== "ArrowDown" && key !== "ArrowUp") return null;
  if (items.length === 0) return null;
  const currentIndex = items.findIndex((item) => idOf(item) === currentId);
  const delta = key === "ArrowDown" ? 1 : -1;
  const nextIndex = Math.min(
    Math.max(currentIndex + delta, 0),
    items.length - 1,
  );
  if (nextIndex === currentIndex) return null;
  return items[nextIndex] ?? null;
}

export function StaffAnnuaire() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);

  // Create company modal
  const [createCompanyOpen, setCreateCompanyOpen] = useState(false);
  const [isCreatingCompany, setIsCreatingCompany] = useState(false);
  const createCompanyForm = useForm<{ name: string }>({
    defaultValues: { name: "" },
  });

  // Edit company modal
  const [editCompany, setEditCompany] = useState<Company | null>(null);
  const [isEditingCompany, setIsEditingCompany] = useState(false);
  const [editCompanyConfirmStep, setEditCompanyConfirmStep] = useState(false);
  const editCompanyForm = useForm<{ name: string }>({
    defaultValues: { name: "" },
  });

  // Delete company modal
  const [deleteCompany, setDeleteCompany] = useState<Company | null>(null);
  const [isDeletingCompany, setIsDeletingCompany] = useState(false);

  // Create employee modal
  const [createEmployeeCompanyId, setCreateEmployeeCompanyId] = useState<
    number | null
  >(null);
  const [isCreatingEmployee, setIsCreatingEmployee] = useState(false);
  const createEmployeeForm = useForm<{
    firstName: string;
    lastName: string;
    email: string;
    role: "admin" | "user" | typeof NO_ROLE_VALUE;
  }>({
    defaultValues: { firstName: "", lastName: "", email: "", role: "user" },
  });
  const createEmployeeMailRequired =
    createEmployeeForm.watch("role") !== NO_ROLE_VALUE;
  // Once the user manually picks a role, the email-domain default stops
  // overriding it.
  const [createEmployeeRoleTouched, setCreateEmployeeRoleTouched] =
    useState(false);

  // Edit employee name modal
  const [editNameEmployee, setEditNameEmployee] = useState<Employee | null>(
    null,
  );
  const [isEditingName, setIsEditingName] = useState(false);
  const editNameForm = useForm<{ firstName: string; lastName: string }>({
    defaultValues: { firstName: "", lastName: "" },
  });

  // Change email modal
  const [changeEmailEmployee, setChangeEmailEmployee] =
    useState<Employee | null>(null);
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [changeEmailConfirmStep, setChangeEmailConfirmStep] = useState(false);
  const changeEmailForm = useForm<{ email: string }>({
    defaultValues: { email: "" },
  });

  // Delete employee modal
  const [deleteEmployee, setDeleteEmployee] = useState<Employee | null>(null);
  const [isDeletingEmployee, setIsDeletingEmployee] = useState(false);

  // Toggle active modal
  const [toggleActiveEmployee, setToggleActiveEmployee] =
    useState<Employee | null>(null);
  const [isTogglingActive, setIsTogglingActive] = useState(false);
  // Deactivation names the successor of each referent role (PO, N28, 30/10)
  const [deactivation, setDeactivation] = useState<{
    employee: Employee;
    alreadyInactive: boolean;
  } | null>(null);

  // Contracts modal
  const [contractsEmployee, setContractsEmployee] = useState<Employee | null>(
    null,
  );
  const [contracts, setContracts] = useState<EmployeeContract[]>([]);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [editMailContract, setEditMailContract] =
    useState<EmployeeContract | null>(null);
  const [isEditingMail, setIsEditingMail] = useState(false);
  const [editMailConfirmOpen, setEditMailConfirmOpen] = useState(false);
  const mailForm = useForm<{ mail: string }>({
    defaultValues: { mail: "" },
    mode: "onTouched",
  });

  // Logo upload (create + edit company)
  const [createLogoFile, setCreateLogoFile] = useState<File | null>(null);
  const [editLogoFile, setEditLogoFile] = useState<File | null>(null);

  // Master-detail selection (3-column layout)
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null,
  );
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(
    null,
  );
  const [selectedContractId, setSelectedContractId] = useState<number | null>(
    null,
  );

  // Row refs so arrow-key navigation can move DOM focus along with the
  // selection.
  const companyRowRefs = useRef(new Map<number, HTMLButtonElement>());
  const employeeRowRefs = useRef(new Map<string, HTMLButtonElement>());
  const contractRowRefs = useRef(new Map<number, HTMLButtonElement>());

  // Returns whether the upload succeeded: callers must not claim overall
  // success (toast + closing the modal) when only the logo leg failed.
  const uploadCompanyLogo = async (
    companyId: number,
    file: File,
  ): Promise<boolean> => {
    const { error } = await apiClient.POST("/api/admin/companies/{id}/logo", {
      params: { path: { id: companyId } },
      body: { logo: file },
      bodySerializer(body) {
        const fd = new FormData();
        if (body?.logo instanceof Blob) {
          fd.set("logo", body.logo);
        }
        return fd;
      },
    });
    if (error) {
      toaster.error({
        title: "Logo non enregistré",
        description: error.error || "Le logo n'a pas pu être téléversé",
      });
      return false;
    }
    return true;
  };

  // `loading` covers the first load only: a refresh after an action leaves the
  // columns mounted, so their lists keep their scroll position.
  const fetchData = useCallback(async () => {
    const [companiesRes, employeesRes] = await Promise.all([
      fetchAllCompanies(),
      fetchAllEmployees(),
    ]);
    if (isNil(companiesRes.companies)) {
      toaster.error({
        title: "Erreur",
        description: "Impossible de charger les sociétés",
      });
    } else {
      setCompanies(companiesRes.companies);
    }
    if (isNil(employeesRes.employees)) {
      toaster.error({
        title: "Erreur",
        description: "Impossible de charger les collaborateurs",
      });
    } else {
      setEmployees(employeesRes.employees);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Company CRUD
  const handleCreateCompany = async (data: { name: string }) => {
    setIsCreatingCompany(true);
    const { data: created, error } = await apiClient.POST(
      "/api/admin/companies",
      { body: { name: data.name } },
    );
    if (error) {
      toaster.error({
        title: "Erreur",
        description: "Impossible de créer la société",
      });
      setIsCreatingCompany(false);
      return;
    }
    // The company is created regardless; a failed logo upload (its own error
    // toast already fired above) doesn't undo that, so say so rather than
    // claiming an unqualified success.
    const logoOk = createLogoFile
      ? await uploadCompanyLogo(created.data.company.id, createLogoFile)
      : true;
    toaster.success({
      title: "Société créée",
      description: logoOk
        ? undefined
        : "Le logo n'a pas pu être envoyé ; modifiez la société pour réessayer.",
    });
    setCreateCompanyOpen(false);
    createCompanyForm.reset();
    setCreateLogoFile(null);
    fetchData();
    setIsCreatingCompany(false);
  };

  const handleEditCompany = async (data: { name: string }) => {
    if (!editCompany) return;
    // A company under contract on a commissioned yard requires a confirmation
    // before its changes are saved.
    if (editCompany.hasCommissionedContract && !editCompanyConfirmStep) {
      setEditCompanyConfirmStep(true);
      return;
    }
    setIsEditingCompany(true);
    const { error } = await apiClient.PATCH("/api/admin/companies/{id}", {
      params: { path: { id: editCompany.id } },
      body: { name: data.name },
    });
    if (error) {
      toaster.error({
        title: "Erreur",
        description: "Impossible de modifier la société",
      });
      setIsEditingCompany(false);
      return;
    }
    const logoOk = editLogoFile
      ? await uploadCompanyLogo(editCompany.id, editLogoFile)
      : true;
    if (logoOk) {
      toaster.success({ title: "Société modifiée" });
      setEditCompany(null);
      setEditCompanyConfirmStep(false);
      setEditLogoFile(null);
    }
    // The name change is saved either way; on a logo failure (its own error
    // toast already fired above) the modal stays open with the same file
    // still picked, so "Enregistrer" can be retried without reselecting it.
    fetchData();
    setIsEditingCompany(false);
  };

  const handleDeleteCompany = async () => {
    if (!deleteCompany) return;
    setIsDeletingCompany(true);
    const { error } = await apiClient.DELETE("/api/admin/companies/{id}", {
      params: { path: { id: deleteCompany.id } },
    });
    if (error) {
      toaster.error({ title: "Impossible", description: error.error });
    } else {
      toaster.success({ title: "Société supprimée" });
      setDeleteCompany(null);
      fetchData();
    }
    setIsDeletingCompany(false);
  };

  // Employee CRUD
  const handleCreateEmployee = async (data: {
    firstName: string;
    lastName: string;
    email: string;
    role: "admin" | "user" | typeof NO_ROLE_VALUE;
  }) => {
    if (!createEmployeeCompanyId) return;
    // A role grants tool access, which requires login credentials: assigning
    // one without an email is not a valid state.
    if (data.role !== NO_ROLE_VALUE && !data.email.trim()) {
      toaster.error({
        title: "Erreur",
        description: "Le mail de login est requis pour attribuer un rôle",
      });
      return;
    }
    setIsCreatingEmployee(true);
    const { error, response } = await apiClient.POST("/api/admin/employees", {
      body: {
        companyId: createEmployeeCompanyId,
        email: data.email.trim() || undefined,
        firstName: data.firstName,
        // Last name is always stored uppercase; first name is free-form.
        lastName: data.lastName.toUpperCase(),
        role: data.role === NO_ROLE_VALUE ? undefined : data.role,
      },
    });
    if (response.status === 409 || error?.error === INVALID_EMAIL_MESSAGE) {
      createEmployeeForm.setError("email", { message: error?.error });
    } else if (error) {
      toaster.error({
        title: "Erreur",
        description: error.error || "Impossible de créer le collaborateur",
      });
    } else {
      toaster.success({ title: "Collaborateur ajouté" });
      setCreateEmployeeCompanyId(null);
      createEmployeeForm.reset();
      setCreateEmployeeRoleTouched(false);
      fetchData();
    }
    setIsCreatingEmployee(false);
  };

  const handleEditName = async (data: {
    firstName: string;
    lastName: string;
  }) => {
    if (!editNameEmployee) return;
    setIsEditingName(true);
    const { error } = await apiClient.PATCH("/api/admin/employees/{id}", {
      params: { path: { id: editNameEmployee.id } },
      // Last name is always stored uppercase; first name is free-form.
      body: {
        firstName: data.firstName,
        lastName: data.lastName.toUpperCase(),
      },
    });
    if (error) {
      toaster.error({ title: "Erreur", description: "Impossible de modifier" });
    } else {
      toaster.success({ title: "Informations modifiées" });
      setEditNameEmployee(null);
      fetchData();
    }
    setIsEditingName(false);
  };

  const handleChangeEmail = async (data: { email: string }) => {
    if (!changeEmailEmployee) return;
    if (!changeEmailConfirmStep) {
      setChangeEmailConfirmStep(true);
      return;
    }
    setIsChangingEmail(true);
    const { error } = await apiClient.PATCH(
      "/api/admin/employees/{id}/change-email",
      {
        params: { path: { id: changeEmailEmployee.id } },
        body: { email: data.email },
      },
    );
    if (error) {
      // Back to the field: the refusal shows next to it.
      setChangeEmailConfirmStep(false);
      changeEmailForm.setError("email", {
        message: error.error || "Impossible de modifier le mail",
      });
    } else {
      toaster.success({ title: "Mail de login modifié" });
      setChangeEmailEmployee(null);
      setChangeEmailConfirmStep(false);
      changeEmailForm.reset();
      fetchData();
    }
    setIsChangingEmail(false);
  };

  const handleDeleteEmployee = async () => {
    if (!deleteEmployee) return;
    setIsDeletingEmployee(true);
    const { error } = await apiClient.DELETE("/api/admin/employees/{id}", {
      params: { path: { id: deleteEmployee.id } },
    });
    if (error) {
      toaster.error({ title: "Impossible", description: error.error });
    } else {
      toaster.success({ title: "Collaborateur supprimé" });
      setDeleteEmployee(null);
      fetchData();
    }
    setIsDeletingEmployee(false);
  };

  const handleToggleActive = async () => {
    if (!toggleActiveEmployee) return;
    setIsTogglingActive(true);
    const { error } = await apiClient.PATCH("/api/admin/employees/{id}", {
      params: { path: { id: toggleActiveEmployee.id } },
      body: { isActive: !toggleActiveEmployee.isActive },
    });
    if (error) {
      toaster.error({
        title: "Erreur",
        description: "Impossible de modifier le statut",
      });
    } else {
      toaster.success({
        title: toggleActiveEmployee.isActive
          ? "Collaborateur désactivé"
          : "Collaborateur activé",
      });
      setToggleActiveEmployee(null);
      fetchData();
      // Contract activity follows the employee's, so the open column is stale.
      if (contractsEmployee?.id === toggleActiveEmployee.id) {
        openContracts(contractsEmployee);
      }
    }
    setIsTogglingActive(false);
  };

  // Contracts
  const openContracts = async (emp: Employee) => {
    setContractsEmployee(emp);
    if (emp.id !== contractsEmployee?.id) setLoadingContracts(true);
    const { data, error } = await apiClient.GET(
      "/api/admin/employees/{id}/contracts",
      { params: { path: { id: emp.id } } },
    );
    if (error) {
      toaster.error({
        title: "Erreur",
        description: "Impossible de charger les contrats",
      });
    } else {
      setContracts(data.data.contracts);
    }
    setLoadingContracts(false);
  };

  const handleUpdateMail = async (data: { mail: string }) => {
    if (!editMailContract || !contractsEmployee) return;
    // Editing the correspondence mail is free while the yard is in preparation;
    // for a commissioned/archived/closed yard a confirmation step is required.
    const isPreparation =
      getYardState(contractYard(editMailContract)) === "preparation";
    if (!isPreparation && !editMailConfirmOpen) {
      setEditMailConfirmOpen(true);
      return;
    }
    setIsEditingMail(true);
    const { error } = await apiClient.PATCH(
      "/api/admin/employees/{id}/contracts/{employeeContractId}/mail",
      {
        params: {
          path: {
            id: contractsEmployee.id,
            employeeContractId: editMailContract.employeeContractId,
          },
        },
        body: { mail: data.mail || null },
      },
    );
    if (error) {
      // Back to the field: the refusal shows next to it.
      setEditMailConfirmOpen(false);
      mailForm.setError("mail", { message: error.error });
    } else {
      toaster.success({ title: "Mail de correspondance modifié" });
      setEditMailContract(null);
      setEditMailConfirmOpen(false);
      mailForm.reset();
      if (contractsEmployee) {
        await openContracts(contractsEmployee);
      }
    }
    setIsEditingMail(false);
  };

  const mailError = mailForm.formState.errors.mail?.message;
  const createLastNameError =
    createEmployeeForm.formState.errors.lastName?.message;
  const createFirstNameError =
    createEmployeeForm.formState.errors.firstName?.message;
  const editLastNameError = editNameForm.formState.errors.lastName?.message;
  const editFirstNameError = editNameForm.formState.errors.firstName?.message;
  const createEmailError = createEmployeeForm.formState.errors.email?.message;
  const changeEmailError = changeEmailForm.formState.errors.email?.message;

  const employeesByCompany = (companyId: number) =>
    employees
      .filter((e) => e.companyId === companyId)
      .sort((a, b) => {
        const nameA = `${a.lastName ?? ""} ${a.firstName ?? ""}`.trim();
        const nameB = `${b.lastName ?? ""} ${b.firstName ?? ""}`.trim();
        return nameA.localeCompare(nameB, "fr");
      });

  const sortedCompanies = [...companies].sort((a, b) =>
    a.name.localeCompare(b.name, "fr"),
  );
  const selectedCompany = selectedCompanyId
    ? (companies.find((c) => c.id === selectedCompanyId) ?? null)
    : null;
  const selectedCompanyEmployees = selectedCompanyId
    ? employeesByCompany(selectedCompanyId)
    : [];
  const employeesColumnTitle = selectedCompany
    ? `Collaborateurs — ${selectedCompany.name}`
    : "Collaborateurs";
  const selectedEmployee =
    employees.find((e) => e.id === selectedEmployeeId) ?? null;
  const sortedContracts = [...contracts].sort(
    (a, b) =>
      YARD_STATE_ORDER.indexOf(getYardState(contractYard(a))) -
      YARD_STATE_ORDER.indexOf(getYardState(contractYard(b))),
  );
  // Contracts split into one sub-column per yard state, keeping only the
  // states that actually have contracts (in their canonical order).
  const contractsByState = YARD_STATE_ORDER.map((state) => ({
    state,
    contracts: sortedContracts.filter(
      (c) => getYardState(contractYard(c)) === state,
    ),
  })).filter((group) => group.contracts.length > 0);

  const selectCompany = (id: number) => {
    setSelectedCompanyId(id);
    setSelectedEmployeeId(null);
    setSelectedContractId(null);
    setContractsEmployee(null);
    setContracts([]);
  };

  const selectEmployee = (emp: Employee) => {
    setSelectedEmployeeId(emp.id);
    setSelectedContractId(null);
    openContracts(emp);
  };

  if (loading) {
    return (
      <div className="flex min-h-full items-center justify-center bg-zinc-50">
        <p className="text-zinc-400">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden bg-zinc-50">
      <header className="shrink-0 border-b border-zinc-200 bg-white px-6 py-4">
        <div className="mx-auto flex max-w-7xl items-center gap-3">
          <Button plain onClick={() => navigate("/staff")} aria-label="Retour">
            <ArrowLeftIcon className="size-4" />
          </Button>
          <h1 className="text-lg font-semibold text-zinc-900">
            Annuaire sociétés et collaborateurs
          </h1>
        </div>
      </header>

      {/* Main: 3-column master-detail */}
      <main className="mx-auto grid min-h-0 w-full max-w-7xl flex-1 grid-cols-1 gap-4 px-6 py-6 lg:grid-cols-[18rem_22rem_1fr]">
        {/* Column 1: Sociétés */}
        <section className="flex min-h-0 flex-col rounded-lg border border-zinc-200 bg-white">
          <div className="flex shrink-0 items-center justify-between border-b border-zinc-100 px-4 py-3">
            <h2 className="font-semibold text-sm text-zinc-900">Sociétés</h2>
            <Button
              outline
              onClick={() => {
                createCompanyForm.reset();
                setCreateLogoFile(null);
                setCreateCompanyOpen(true);
              }}
            >
              <PlusIcon data-slot="icon" className="size-3" />
              Ajouter
            </Button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {sortedCompanies.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-zinc-400">
                Aucune société
              </p>
            ) : (
              sortedCompanies.map((company) => (
                <div
                  key={company.id}
                  className={cn(
                    "group relative border-zinc-50 border-b last:border-0",
                    selectedCompanyId === company.id
                      ? "bg-blue-50"
                      : "hover:bg-zinc-50",
                  )}
                >
                  <button
                    type="button"
                    ref={(el) => {
                      if (el) companyRowRefs.current.set(company.id, el);
                      else companyRowRefs.current.delete(company.id);
                    }}
                    onClick={(e) => {
                      selectCompany(company.id);
                      e.currentTarget.focus();
                    }}
                    onKeyDown={(e) => {
                      const next = moveSelection(
                        e.key,
                        sortedCompanies,
                        selectedCompanyId,
                        (c) => c.id,
                      );
                      if (!next) return;
                      e.preventDefault();
                      selectCompany(next.id);
                      companyRowRefs.current.get(next.id)?.focus();
                    }}
                    className="flex w-full items-center gap-3 px-4 py-3 pr-16 text-left"
                  >
                    {company.logoUrl ? (
                      <img
                        src={company.logoUrl}
                        alt={company.name}
                        className="size-8 shrink-0 rounded object-contain"
                      />
                    ) : (
                      <span className="flex size-8 shrink-0 items-center justify-center rounded bg-zinc-100 font-semibold text-xs text-zinc-400">
                        {company.name.slice(0, 2).toUpperCase()}
                      </span>
                    )}
                    <span
                      className="min-w-0 flex-1 truncate font-medium text-sm text-zinc-800"
                      title={company.name}
                    >
                      {company.name}
                    </span>
                  </button>
                  <span className="absolute top-1/2 right-3 flex -translate-y-1/2 items-center gap-0.5 opacity-0 group-hover:opacity-100">
                    <button
                      type="button"
                      onClick={() => {
                        editCompanyForm.reset({ name: company.name });
                        setEditCompanyConfirmStep(false);
                        setEditLogoFile(null);
                        setEditCompany(company);
                      }}
                      aria-label="Modifier la société"
                      className="rounded p-1 text-zinc-400 hover:text-zinc-700"
                    >
                      <PencilSimpleIcon className="size-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteCompany(company)}
                      aria-label="Supprimer la société"
                      className="rounded p-1 text-red-300 hover:text-red-500"
                    >
                      <TrashIcon className="size-4" />
                    </button>
                  </span>
                </div>
              ))
            )}
          </div>
        </section>

        {/* Column 2: Collaborateurs */}
        <section className="flex min-h-0 flex-col rounded-lg border border-zinc-200 bg-white">
          <div className="flex shrink-0 items-center justify-between border-b border-zinc-100 px-4 py-3">
            <h2
              className="truncate font-semibold text-sm text-zinc-900"
              title={employeesColumnTitle}
            >
              {employeesColumnTitle}
            </h2>
            {selectedCompany && (
              <Button
                outline
                onClick={() => {
                  createEmployeeForm.reset();
                  setCreateEmployeeRoleTouched(false);
                  setCreateEmployeeCompanyId(selectedCompany.id);
                }}
              >
                <PlusIcon data-slot="icon" className="size-3" />
                Ajouter
              </Button>
            )}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {!selectedCompany ? (
              <p className="px-4 py-6 text-center text-sm text-zinc-400">
                Sélectionnez une société
              </p>
            ) : selectedCompanyEmployees.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-zinc-400">
                Aucun collaborateur
              </p>
            ) : (
              selectedCompanyEmployees.map((emp) => (
                <div
                  key={emp.id}
                  className={cn(
                    "group relative border-zinc-50 border-b last:border-0",
                    selectedEmployeeId === emp.id
                      ? "bg-blue-50"
                      : "hover:bg-zinc-50",
                  )}
                >
                  <button
                    type="button"
                    ref={(el) => {
                      if (el) employeeRowRefs.current.set(emp.id, el);
                      else employeeRowRefs.current.delete(emp.id);
                    }}
                    onClick={(e) => {
                      selectEmployee(emp);
                      e.currentTarget.focus();
                    }}
                    onKeyDown={(e) => {
                      const next = moveSelection(
                        e.key,
                        selectedCompanyEmployees,
                        selectedEmployeeId,
                        (item) => item.id,
                      );
                      if (!next) return;
                      e.preventDefault();
                      selectEmployee(next);
                      employeeRowRefs.current.get(next.id)?.focus();
                    }}
                    className="flex w-full flex-col gap-1 px-4 py-3 text-left"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="min-w-0 flex-1 truncate font-medium text-sm text-zinc-800"
                        title={employeeDisplayName(emp)}
                      >
                        {employeeDisplayName(emp)}
                      </span>
                      <span
                        className={cn(
                          "shrink-0 rounded-full px-2 py-0.5 text-xs font-medium",
                          emp.isActive
                            ? "bg-green-100 text-green-700"
                            : "bg-zinc-100 text-zinc-500",
                        )}
                      >
                        {emp.isActive ? "Actif" : "Inactif"}
                      </span>
                      {emp.pendingReferentRoles > 0 && (
                        <span
                          className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                          title="Désactivé mais encore référent : désignez ses remplaçants (colonne Contrats)"
                        >
                          Référent à remplacer
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                      <span className="truncate" title={emp.email ?? undefined}>
                        {emp.email ?? "—"}
                      </span>
                      <span className="text-zinc-300">·</span>
                      <span className="shrink-0">
                        {isNotNil(emp.role)
                          ? EMPLOYEE_ROLE_LABELS[emp.role]
                          : NO_ROLE_LABEL}
                      </span>
                    </div>
                    <span className="text-xs text-zinc-400">
                      {connectionLabel(emp)}
                    </span>
                  </button>
                  <span className="absolute right-3 bottom-2 flex shrink-0 items-center gap-0.5 opacity-0 group-hover:opacity-100">
                    <button
                      type="button"
                      onClick={() => {
                        editNameForm.reset({
                          firstName: emp.firstName ?? "",
                          lastName: emp.lastName ?? "",
                        });
                        setEditNameEmployee(emp);
                      }}
                      aria-label="Modifier nom/prénom"
                      className="rounded p-1 text-zinc-400 hover:text-zinc-700"
                    >
                      <PencilSimpleIcon className="size-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        changeEmailForm.reset({ email: emp.email ?? "" });
                        setChangeEmailConfirmStep(false);
                        setChangeEmailEmployee(emp);
                      }}
                      aria-label="Modifier le mail de login"
                      className="rounded px-1 text-xs text-zinc-400 hover:text-zinc-700"
                    >
                      @
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        emp.isActive
                          ? setDeactivation({
                              employee: emp,
                              alreadyInactive: false,
                            })
                          : setToggleActiveEmployee(emp)
                      }
                      aria-label="Activer / désactiver"
                      className="rounded px-1 text-xs text-zinc-400 hover:text-zinc-700"
                    >
                      {emp.isActive ? "Off" : "On"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteEmployee(emp)}
                      aria-label="Supprimer le collaborateur"
                      className="rounded p-1 text-red-300 hover:text-red-500"
                    >
                      <TrashIcon className="size-4" />
                    </button>
                  </span>
                </div>
              ))
            )}
          </div>
        </section>

        {/* Column 3: Contrats du collaborateur */}
        <section className="flex min-h-0 min-w-0 flex-col rounded-lg border border-zinc-200 bg-white">
          <div className="shrink-0 border-b border-zinc-100 px-4 py-3">
            <h2 className="truncate font-semibold text-sm text-zinc-900">
              {selectedEmployeeId ? "Contrats du collaborateur" : "Contrats"}
            </h2>
            {isNotNil(selectedEmployee) &&
              selectedEmployee.pendingReferentRoles > 0 && (
                <div className="mt-2 flex items-center justify-between gap-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  <span>
                    Désactivé mais encore référent : ses rôles attendent un
                    remplaçant.
                  </span>
                  <Button
                    outline
                    onClick={() =>
                      setDeactivation({
                        employee: selectedEmployee,
                        alreadyInactive: true,
                      })
                    }
                  >
                    Désigner ses remplaçants
                  </Button>
                </div>
              )}
          </div>
          <div className="min-h-0 flex-1 overflow-auto p-4">
            {!selectedEmployeeId ? (
              <p className="py-6 text-center text-sm text-zinc-400">
                Sélectionnez un collaborateur
              </p>
            ) : loadingContracts ? (
              <p className="py-6 text-center text-sm text-zinc-400">
                Chargement...
              </p>
            ) : contractsByState.length === 0 ? (
              <p className="py-6 text-center text-sm text-zinc-400">
                Aucun contrat
              </p>
            ) : (
              <div className="space-y-5">
                {contractsByState.map(
                  ({ state, contracts: stateContracts }) => (
                    <div key={state}>
                      <div className="mb-2 flex items-center gap-2">
                        <span
                          className={cn(
                            "rounded-full px-2 py-0.5 text-xs font-medium",
                            yardStateBadgeClass[state],
                          )}
                        >
                          {YARD_STATE_LABELS[state]}
                        </span>
                        <span className="text-xs text-zinc-400">
                          {stateContracts.length}
                        </span>
                      </div>
                      <ul className="space-y-2">
                        {stateContracts.map((contract) => (
                          <li
                            key={contract.employeeContractId}
                            className={cn(
                              "relative rounded-md border border-zinc-100 bg-zinc-50/50",
                              selectedContractId ===
                                contract.employeeContractId &&
                                "border-blue-300 bg-blue-50",
                            )}
                          >
                            <button
                              type="button"
                              ref={(el) => {
                                if (el) {
                                  contractRowRefs.current.set(
                                    contract.employeeContractId,
                                    el,
                                  );
                                } else {
                                  contractRowRefs.current.delete(
                                    contract.employeeContractId,
                                  );
                                }
                              }}
                              onClick={(e) => {
                                setSelectedContractId(
                                  contract.employeeContractId,
                                );
                                e.currentTarget.focus();
                              }}
                              onKeyDown={(e) => {
                                const next = moveSelection(
                                  e.key,
                                  sortedContracts,
                                  selectedContractId,
                                  (c) => c.employeeContractId,
                                );
                                if (!next) return;
                                e.preventDefault();
                                setSelectedContractId(next.employeeContractId);
                                contractRowRefs.current
                                  .get(next.employeeContractId)
                                  ?.focus();
                              }}
                              className="block w-full px-3 py-2 pr-9 text-left"
                            >
                              <div className="flex items-center gap-2">
                                <p
                                  className="min-w-0 flex-1 truncate font-medium text-sm text-zinc-800"
                                  title={contract.yardName}
                                >
                                  {contract.yardName}
                                </p>
                                <span
                                  className={cn(
                                    "shrink-0 rounded-full px-2 py-0.5 text-xs font-medium",
                                    contract.isActive
                                      ? "bg-green-100 text-green-700"
                                      : "bg-zinc-100 text-zinc-500",
                                  )}
                                >
                                  {contract.isActive ? "Actif" : "Inactif"}
                                </span>
                              </div>
                              <p className="text-xs text-zinc-500">
                                {contractTypeLabel(contract.contractType)}
                              </p>
                              <p
                                className="truncate text-xs text-zinc-500"
                                title={
                                  getYardStateDateLabel(
                                    contractYard(contract),
                                  ) ?? undefined
                                }
                              >
                                {getYardStateDateLabel(contractYard(contract))}
                              </p>
                              <p
                                className="mt-1 truncate text-xs text-zinc-500"
                                title={contract.mail ?? undefined}
                              >
                                {contract.mail ?? (
                                  <span className="text-zinc-300">
                                    Pas de mail
                                  </span>
                                )}
                              </p>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                mailForm.reset({ mail: contract.mail ?? "" });
                                setEditMailConfirmOpen(false);
                                setEditMailContract(contract);
                              }}
                              className="absolute right-2 bottom-2 shrink-0 rounded p-1 text-zinc-400 hover:text-zinc-700"
                              aria-label="Modifier le mail de correspondance"
                            >
                              <PencilSimpleIcon className="size-4" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ),
                )}
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Modal: Créer une société */}
      <Modal.Root
        open={createCompanyOpen}
        onOpenChange={(d) => setCreateCompanyOpen(d.open)}
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content>
              <Modal.Header>
                <Modal.Title>Ajouter une société</Modal.Title>
              </Modal.Header>
              <form
                onSubmit={createCompanyForm.handleSubmit(handleCreateCompany)}
              >
                <Modal.Body className="space-y-4">
                  <div>
                    <label
                      htmlFor="create-company-name"
                      className="mb-1 block text-sm font-medium text-zinc-900"
                    >
                      Nom <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="create-company-name"
                      {...createCompanyForm.register("name")}
                      required
                      className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      placeholder="Nom de la société"
                    />
                  </div>
                  <LogoUploadField
                    id="create-company-logo"
                    file={createLogoFile}
                    onFileChange={setCreateLogoFile}
                  />
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    outline
                    type="button"
                    onClick={() => setCreateCompanyOpen(false)}
                    disabled={isCreatingCompany}
                  >
                    Annuler
                  </Button>
                  <Button type="submit" disabled={isCreatingCompany}>
                    {isCreatingCompany ? "Création..." : "Créer"}
                  </Button>
                </Modal.Footer>
              </form>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>

      {/* Modal: Modifier une société */}
      {editCompany && (
        <Modal.Root
          open
          onOpenChange={(d) => {
            if (!d.open) {
              setEditCompany(null);
              setEditCompanyConfirmStep(false);
              setEditLogoFile(null);
            }
          }}
        >
          <Modal.Portal>
            <Modal.Backdrop />
            <Modal.Positioner>
              <Modal.Content>
                <Modal.Header>
                  <Modal.Title>Modifier la société</Modal.Title>
                </Modal.Header>
                <form
                  onSubmit={editCompanyForm.handleSubmit(handleEditCompany)}
                >
                  <Modal.Body className="space-y-3">
                    <div>
                      <label
                        htmlFor="edit-company-name"
                        className="mb-1 block text-sm font-medium text-zinc-900"
                      >
                        Nom <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="edit-company-name"
                        {...editCompanyForm.register("name")}
                        required
                        className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      />
                    </div>
                    <LogoUploadField
                      id="edit-company-logo"
                      file={editLogoFile}
                      existingLogoUrl={editCompany.logoUrl}
                      onFileChange={setEditLogoFile}
                    />
                    {editCompanyConfirmStep && (
                      <p className="rounded-md bg-amber-50 px-3 py-2 text-amber-800 text-sm">
                        Cette société intervient sur un ou plusieurs chantiers
                        mis en service. Confirmez-vous la modification ?
                      </p>
                    )}
                  </Modal.Body>
                  <Modal.Footer>
                    <Button
                      outline
                      type="button"
                      onClick={() => {
                        if (editCompanyConfirmStep) {
                          setEditCompanyConfirmStep(false);
                        } else {
                          setEditCompany(null);
                        }
                      }}
                      disabled={isEditingCompany}
                    >
                      {editCompanyConfirmStep ? "Retour" : "Annuler"}
                    </Button>
                    <Button type="submit" disabled={isEditingCompany}>
                      {isEditingCompany
                        ? "Modification..."
                        : editCompany.hasCommissionedContract &&
                            !editCompanyConfirmStep
                          ? "Suivant"
                          : "Enregistrer"}
                    </Button>
                  </Modal.Footer>
                </form>
              </Modal.Content>
            </Modal.Positioner>
          </Modal.Portal>
        </Modal.Root>
      )}

      {/* Modal: Supprimer une société */}
      {deleteCompany && (
        <Modal.Root
          open
          onOpenChange={(d) => {
            if (!d.open) setDeleteCompany(null);
          }}
        >
          <Modal.Portal>
            <Modal.Backdrop />
            <Modal.Positioner>
              <Modal.Content>
                <Modal.Header>
                  <Modal.Title>Supprimer la société</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Modal.Description>
                    {deleteCompany.hasCommissionedContract ? (
                      <>
                        La société sélectionnée intervient sur un ou plusieurs
                        chantiers ayant été mis en service. Il n'est pas
                        possible de la supprimer.
                      </>
                    ) : employeesByCompany(deleteCompany.id).length > 0 ? (
                      <>
                        Supprimez les employés de cette société pour pouvoir
                        ensuite supprimer la société.
                      </>
                    ) : (
                      <>
                        Vous êtes sur le point de supprimer la société{" "}
                        <strong>{deleteCompany.name}</strong> ainsi que les
                        contrats qui y sont éventuellement rattachés.
                      </>
                    )}
                  </Modal.Description>
                </Modal.Body>
                <Modal.Footer>
                  {deleteCompany.hasCommissionedContract ||
                  employeesByCompany(deleteCompany.id).length > 0 ? (
                    <Button onClick={() => setDeleteCompany(null)}>
                      Fermer
                    </Button>
                  ) : (
                    <>
                      <Button
                        outline
                        onClick={() => setDeleteCompany(null)}
                        disabled={isDeletingCompany}
                      >
                        Annuler
                      </Button>
                      <Button
                        onClick={handleDeleteCompany}
                        disabled={isDeletingCompany}
                      >
                        {isDeletingCompany ? "Suppression..." : "Supprimer"}
                      </Button>
                    </>
                  )}
                </Modal.Footer>
              </Modal.Content>
            </Modal.Positioner>
          </Modal.Portal>
        </Modal.Root>
      )}

      {/* Modal: Ajouter un collaborateur */}
      {isNotNil(createEmployeeCompanyId) && (
        <Modal.Root
          open
          onOpenChange={(d) => {
            if (!d.open) setCreateEmployeeCompanyId(null);
          }}
        >
          <Modal.Portal>
            <Modal.Backdrop />
            <Modal.Positioner>
              <Modal.Content>
                <Modal.Header>
                  <Modal.Title>Ajouter un collaborateur</Modal.Title>
                </Modal.Header>
                {/* noValidate: same reason as the correspondence mail form. */}
                <form
                  noValidate
                  onSubmit={createEmployeeForm.handleSubmit(
                    handleCreateEmployee,
                  )}
                >
                  <Modal.Body className="space-y-4">
                    <div>
                      <label
                        htmlFor="emp-lastname"
                        className="mb-1 block text-sm font-medium text-zinc-900"
                      >
                        Nom <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="emp-lastname"
                        required
                        aria-invalid={isNotNil(createLastNameError)}
                        aria-describedby="emp-lastname-error"
                        {...createEmployeeForm.register("lastName", {
                          validate: checkLastName,
                          // Last name is always stored uppercase; first name
                          // is free-form.
                          onChange: (e) =>
                            createEmployeeForm.setValue(
                              "lastName",
                              e.target.value.toUpperCase(),
                              { shouldValidate: true },
                            ),
                        })}
                        className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      />
                      <FieldError
                        id="emp-lastname-error"
                        message={createLastNameError}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="emp-firstname"
                        className="mb-1 block text-sm font-medium text-zinc-900"
                      >
                        Prénom <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="emp-firstname"
                        required
                        aria-invalid={isNotNil(createFirstNameError)}
                        aria-describedby="emp-firstname-error"
                        {...createEmployeeForm.register("firstName", {
                          validate: checkFirstName,
                        })}
                        className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      />
                      <FieldError
                        id="emp-firstname-error"
                        message={createFirstNameError}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="emp-email"
                        className="mb-1 block text-sm font-medium text-zinc-900"
                      >
                        Mail de login{" "}
                        {createEmployeeMailRequired && (
                          <span className="text-red-500">*</span>
                        )}
                      </label>
                      <input
                        id="emp-email"
                        type="email"
                        aria-required={createEmployeeMailRequired}
                        aria-invalid={isNotNil(createEmailError)}
                        aria-describedby="emp-email-note"
                        {...createEmployeeForm.register("email", {
                          validate: checkMailShape,
                          // Preselect the role from the email domain until
                          // the user picks one explicitly.
                          onChange: (e) => {
                            if (!createEmployeeRoleTouched) {
                              createEmployeeForm.setValue(
                                "role",
                                defaultRoleForEmail(e.target.value),
                              );
                            }
                          },
                        })}
                        placeholder={
                          createEmployeeMailRequired
                            ? "email@societe.fr"
                            : "email@societe.fr (facultatif)"
                        }
                        className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      />
                      <p
                        id="emp-email-note"
                        className={cn(
                          "mt-1 min-h-4 text-xs",
                          isNil(createEmailError)
                            ? "text-zinc-500"
                            : "text-red-600",
                        )}
                      >
                        {createEmailError ??
                          (createEmployeeMailRequired &&
                            "Obligatoire pour un collaborateur qui a un rôle.")}
                      </p>
                    </div>
                    <div>
                      <label
                        htmlFor="emp-role"
                        className="mb-1 block text-sm font-medium text-zinc-900"
                      >
                        Rôle
                      </label>
                      <select
                        id="emp-role"
                        {...createEmployeeForm.register("role", {
                          onChange: () => setCreateEmployeeRoleTouched(true),
                        })}
                        className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      >
                        <option value="user">
                          {EMPLOYEE_ROLE_LABELS.user}
                        </option>
                        <option value="admin">
                          {EMPLOYEE_ROLE_LABELS.admin}
                        </option>
                        <option value={NO_ROLE_VALUE}>
                          {`${NO_ROLE_LABEL} (contact)`}
                        </option>
                      </select>
                    </div>
                  </Modal.Body>
                  <Modal.Footer>
                    <Button
                      outline
                      type="button"
                      onClick={() => setCreateEmployeeCompanyId(null)}
                      disabled={isCreatingEmployee}
                    >
                      Annuler
                    </Button>
                    <Button type="submit" disabled={isCreatingEmployee}>
                      {isCreatingEmployee ? "Création..." : "Ajouter"}
                    </Button>
                  </Modal.Footer>
                </form>
              </Modal.Content>
            </Modal.Positioner>
          </Modal.Portal>
        </Modal.Root>
      )}

      {/* Modal: Modifier nom/prénom */}
      {editNameEmployee && (
        <Modal.Root
          open
          onOpenChange={(d) => {
            if (!d.open) setEditNameEmployee(null);
          }}
        >
          <Modal.Portal>
            <Modal.Backdrop />
            <Modal.Positioner>
              <Modal.Content>
                <Modal.Header>
                  <Modal.Title>Modifier nom et prénom</Modal.Title>
                </Modal.Header>
                {/* noValidate: same reason as the correspondence mail form. */}
                <form
                  noValidate
                  onSubmit={editNameForm.handleSubmit(handleEditName)}
                >
                  <Modal.Body className="space-y-4">
                    <div>
                      <label
                        htmlFor="edit-lastname"
                        className="mb-1 block text-sm font-medium text-zinc-900"
                      >
                        Nom <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="edit-lastname"
                        required
                        aria-invalid={isNotNil(editLastNameError)}
                        aria-describedby="edit-lastname-error"
                        {...editNameForm.register("lastName", {
                          validate: checkLastName,
                          // Last name is always stored uppercase; first name
                          // is free-form.
                          onChange: (e) =>
                            editNameForm.setValue(
                              "lastName",
                              e.target.value.toUpperCase(),
                              { shouldValidate: true },
                            ),
                        })}
                        className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      />
                      <FieldError
                        id="edit-lastname-error"
                        message={editLastNameError}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="edit-firstname"
                        className="mb-1 block text-sm font-medium text-zinc-900"
                      >
                        Prénom <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="edit-firstname"
                        required
                        aria-invalid={isNotNil(editFirstNameError)}
                        aria-describedby="edit-firstname-error"
                        {...editNameForm.register("firstName", {
                          validate: checkFirstName,
                        })}
                        className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      />
                      <FieldError
                        id="edit-firstname-error"
                        message={editFirstNameError}
                      />
                    </div>
                  </Modal.Body>
                  <Modal.Footer>
                    <Button
                      outline
                      type="button"
                      onClick={() => setEditNameEmployee(null)}
                      disabled={isEditingName}
                    >
                      Annuler
                    </Button>
                    <Button type="submit" disabled={isEditingName}>
                      {isEditingName ? "Modification..." : "Enregistrer"}
                    </Button>
                  </Modal.Footer>
                </form>
              </Modal.Content>
            </Modal.Positioner>
          </Modal.Portal>
        </Modal.Root>
      )}

      {/* Modal: Modifier mail de login */}
      {changeEmailEmployee && (
        <Modal.Root
          open
          onOpenChange={(d) => {
            if (!d.open) {
              setChangeEmailEmployee(null);
              setChangeEmailConfirmStep(false);
            }
          }}
        >
          <Modal.Portal>
            <Modal.Backdrop />
            <Modal.Positioner>
              <Modal.Content>
                <Modal.Header>
                  <Modal.Title>
                    {changeEmailConfirmStep
                      ? "Confirmer la modification"
                      : "Modifier le mail de login"}
                  </Modal.Title>
                </Modal.Header>
                {/* noValidate: same reason as the correspondence mail form. */}
                <form
                  noValidate
                  onSubmit={changeEmailForm.handleSubmit(handleChangeEmail)}
                >
                  <Modal.Body className="space-y-3">
                    {!changeEmailConfirmStep ? (
                      <div>
                        <label
                          htmlFor="change-email"
                          className="mb-1 block text-sm font-medium text-zinc-900"
                        >
                          Nouveau mail de login
                        </label>
                        <input
                          id="change-email"
                          type="email"
                          aria-invalid={isNotNil(changeEmailError)}
                          aria-describedby="change-email-error"
                          {...changeEmailForm.register("email", {
                            validate: (mail) =>
                              isValidEmail(mail) || INVALID_EMAIL_MESSAGE,
                          })}
                          required
                          className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        />
                        <p
                          id="change-email-error"
                          className="mt-1 text-red-600 text-sm"
                        >
                          {changeEmailError}
                        </p>
                      </div>
                    ) : (
                      <Modal.Description>
                        {changeEmailEmployee.loginStatus === "active" && (
                          <>
                            Vous êtes sur le point de modifier le mail de login
                            d'un utilisateur. Un mail de création de mot de
                            passe sera envoyé à la nouvelle adresse et le mot de
                            passe actuel sera réinitialisé.
                          </>
                        )}
                        {changeEmailEmployee.loginStatus === "invited" && (
                          <>
                            Vous êtes sur le point de modifier le mail de login
                            d'un utilisateur invité. Un mail de création de mot
                            de passe sera envoyé à la nouvelle adresse.
                          </>
                        )}
                        {changeEmailEmployee.loginStatus === "not_invited" && (
                          <>Confirmer la modification de mail de login ?</>
                        )}
                      </Modal.Description>
                    )}
                  </Modal.Body>
                  <Modal.Footer>
                    <Button
                      outline
                      type="button"
                      onClick={() => {
                        if (changeEmailConfirmStep) {
                          setChangeEmailConfirmStep(false);
                        } else {
                          setChangeEmailEmployee(null);
                        }
                      }}
                      disabled={isChangingEmail}
                    >
                      {changeEmailConfirmStep
                        ? "Annuler les modifications"
                        : "Annuler"}
                    </Button>
                    <Button type="submit" disabled={isChangingEmail}>
                      {isChangingEmail
                        ? "En cours..."
                        : changeEmailConfirmStep
                          ? "Enregistrer"
                          : "Suivant"}
                    </Button>
                  </Modal.Footer>
                </form>
              </Modal.Content>
            </Modal.Positioner>
          </Modal.Portal>
        </Modal.Root>
      )}

      {/* Modal: Supprimer un collaborateur */}
      {deleteEmployee && (
        <Modal.Root
          open
          onOpenChange={(d) => {
            if (!d.open) setDeleteEmployee(null);
          }}
        >
          <Modal.Portal>
            <Modal.Backdrop />
            <Modal.Positioner>
              <Modal.Content>
                <Modal.Header>
                  <Modal.Title>Supprimer un collaborateur</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Modal.Description>
                    {deleteEmployee.hasCommissionedContract ? (
                      <>
                        Il n'est pas possible de supprimer{" "}
                        <strong>{employeeDisplayName(deleteEmployee)}</strong> :
                        ce collaborateur est associé à un ou plusieurs chantiers
                        ayant été mis en service.
                      </>
                    ) : (
                      <>
                        Vous êtes sur le point de supprimer{" "}
                        <strong>{employeeDisplayName(deleteEmployee)}</strong>.
                        Il disparaîtra des chantiers et des équipes
                        correspondantes.
                      </>
                    )}
                  </Modal.Description>
                </Modal.Body>
                <Modal.Footer>
                  {deleteEmployee.hasCommissionedContract ? (
                    <Button onClick={() => setDeleteEmployee(null)}>
                      Fermer
                    </Button>
                  ) : (
                    <>
                      <Button
                        outline
                        onClick={() => setDeleteEmployee(null)}
                        disabled={isDeletingEmployee}
                      >
                        Annuler
                      </Button>
                      <Button
                        onClick={handleDeleteEmployee}
                        disabled={isDeletingEmployee}
                      >
                        {isDeletingEmployee ? "Suppression..." : "Supprimer"}
                      </Button>
                    </>
                  )}
                </Modal.Footer>
              </Modal.Content>
            </Modal.Positioner>
          </Modal.Portal>
        </Modal.Root>
      )}

      {isNotNil(deactivation) && (
        <DeactivateCollaboratorModal
          open
          onOpenChange={(open) => {
            if (!open) setDeactivation(null);
          }}
          employee={{
            id: deactivation.employee.id,
            name: employeeDisplayName(deactivation.employee),
          }}
          alreadyInactive={deactivation.alreadyInactive}
          onSuccess={() => {
            fetchData();
            // Contract activity follows the employee's, so the open column is stale.
            if (contractsEmployee?.id === deactivation.employee.id) {
              openContracts(contractsEmployee);
            }
          }}
        />
      )}

      {/* Modal: réactivation */}
      {toggleActiveEmployee && (
        <Modal.Root
          open
          onOpenChange={(d) => {
            if (!d.open) setToggleActiveEmployee(null);
          }}
        >
          <Modal.Portal>
            <Modal.Backdrop />
            <Modal.Positioner>
              <Modal.Content>
                <Modal.Header>
                  <Modal.Title>
                    {toggleActiveEmployee.isActive ? "Désactiver" : "Activer"}{" "}
                    le collaborateur
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Modal.Description>
                    Confirmer la{" "}
                    {toggleActiveEmployee.isActive
                      ? "désactivation"
                      : "réactivation"}{" "}
                    de{" "}
                    <strong>{employeeDisplayName(toggleActiveEmployee)}</strong>{" "}
                    ?
                  </Modal.Description>
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    outline
                    onClick={() => setToggleActiveEmployee(null)}
                    disabled={isTogglingActive}
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={handleToggleActive}
                    disabled={isTogglingActive}
                  >
                    {isTogglingActive
                      ? "En cours..."
                      : toggleActiveEmployee.isActive
                        ? "Désactiver"
                        : "Activer"}
                  </Button>
                </Modal.Footer>
              </Modal.Content>
            </Modal.Positioner>
          </Modal.Portal>
        </Modal.Root>
      )}

      {/* Modal: Modifier le mail de correspondance d'un contrat */}
      {editMailContract && (
        <Modal.Root
          open
          onOpenChange={(d) => {
            if (!d.open) {
              setEditMailContract(null);
              setEditMailConfirmOpen(false);
              mailForm.reset();
            }
          }}
        >
          <Modal.Portal>
            <Modal.Backdrop />
            <Modal.Positioner>
              <Modal.Content>
                <Modal.Header>
                  <Modal.Title>Mail de correspondance</Modal.Title>
                </Modal.Header>
                {/* noValidate: the browser's bubble is in its own language and
                    accepts a domain without extension. */}
                <form
                  noValidate
                  onSubmit={mailForm.handleSubmit(handleUpdateMail)}
                >
                  <Modal.Body className="space-y-4">
                    {!editMailConfirmOpen ? (
                      <div>
                        <p className="mb-3 text-sm text-zinc-600">
                          Mail de correspondance pour le chantier{" "}
                          <strong>{editMailContract.yardName}</strong> (
                          {contractTypeLabel(editMailContract.contractType)})
                        </p>
                        <input
                          id="contract-mail"
                          type="email"
                          aria-invalid={isNotNil(mailError)}
                          aria-describedby="contract-mail-error"
                          {...mailForm.register("mail", {
                            validate: checkMailShape,
                          })}
                          className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          placeholder="laisser vide pour supprimer"
                        />
                        <p
                          id="contract-mail-error"
                          className="mt-1 text-red-600 text-sm"
                        >
                          {mailError}
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-zinc-700">
                        Vous êtes sur le point de modifier le mail de
                        correspondance pour le chantier{" "}
                        <strong>{editMailContract.yardName}</strong> (état :{" "}
                        {
                          YARD_STATE_LABELS[
                            getYardState(contractYard(editMailContract))
                          ]
                        }
                        ). Confirmer ?
                      </p>
                    )}
                  </Modal.Body>
                  <Modal.Footer>
                    <Button
                      outline
                      type="button"
                      onClick={() => {
                        if (editMailConfirmOpen) {
                          setEditMailConfirmOpen(false);
                        } else {
                          setEditMailContract(null);
                          mailForm.reset();
                        }
                      }}
                      disabled={isEditingMail}
                    >
                      {editMailConfirmOpen ? "Retour" : "Annuler"}
                    </Button>
                    <Button type="submit" disabled={isEditingMail}>
                      {isEditingMail
                        ? "En cours..."
                        : editMailConfirmOpen ||
                            getYardState(contractYard(editMailContract)) ===
                              "preparation"
                          ? "Enregistrer"
                          : "Suivant"}
                    </Button>
                  </Modal.Footer>
                </form>
              </Modal.Content>
            </Modal.Positioner>
          </Modal.Portal>
        </Modal.Root>
      )}
    </div>
  );
}
